export function formatDateTime(s) {
    if (!s) return "—";
    try {
        const d = new Date(s);
        return d.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return s;
    }
}

export function formatDate(s) {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return s;
    }
}

function _escapeHtml(s) {
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function _inlineMd(s) {
    return s
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\b_(.+?)_\b/g, "<em>$1</em>");
}

function _splitRow(l) {
    let s = l.trim();
    if (s.startsWith("|")) s = s.slice(1);
    if (s.endsWith("|")) s = s.slice(0, -1);
    return s.split("|").map((c) => c.trim());
}

export function renderMarkdownInline(text) {
    if (!text) return "";
    return _escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br/>");
}

// Block-level markdown → HTML: tables, bullet/numbered lists, headings, bold.
// Used for chatbot answers so trained Q&A / RAG replies render cleanly (no raw
// pipes or asterisks) in both the widget and the admin transcripts.
export function renderMarkdown(text) {
    if (!text) return "";
    const lines = _escapeHtml(text).split("\n");
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        // GitHub-style table: a header row followed by a |---|---| separator
        const nextIsSep =
            i + 1 < lines.length &&
            lines[i + 1].includes("-") &&
            /^[\s|:-]+$/.test(lines[i + 1].trim()) &&
            lines[i + 1].includes("|");
        if (trimmed.includes("|") && nextIsSep) {
            const header = _splitRow(trimmed);
            i += 2;
            const rows = [];
            while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
                rows.push(_splitRow(lines[i].trim()));
                i++;
            }
            let t = '<table class="cc-md-table"><thead><tr>';
            t += header.map((h) => `<th>${_inlineMd(h)}</th>`).join("");
            t += "</tr></thead><tbody>";
            for (const r of rows) {
                t +=
                    "<tr>" +
                    header.map((_, ci) => `<td>${_inlineMd(r[ci] || "")}</td>`).join("") +
                    "</tr>";
            }
            t += "</tbody></table>";
            out.push(t);
            continue;
        }

        // Unordered list
        if (/^\s*[-*•]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) {
                items.push(_inlineMd(lines[i].replace(/^\s*[-*•]\s+/, "")));
                i++;
            }
            out.push('<ul class="cc-md-ul">' + items.map((x) => `<li>${x}</li>`).join("") + "</ul>");
            continue;
        }

        // Ordered list
        if (/^\s*\d+[.)]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
                items.push(_inlineMd(lines[i].replace(/^\s*\d+[.)]\s+/, "")));
                i++;
            }
            out.push('<ol class="cc-md-ol">' + items.map((x) => `<li>${x}</li>`).join("") + "</ol>");
            continue;
        }

        // Heading
        const h = trimmed.match(/^(#{1,4})\s+(.*)$/);
        if (h) {
            out.push(`<div class="cc-md-h">${_inlineMd(h[2])}</div>`);
            i++;
            continue;
        }

        // Blank line → paragraph gap
        if (trimmed === "") {
            out.push('<div class="cc-md-gap"></div>');
            i++;
            continue;
        }

        // Plain paragraph line
        out.push(`<div class="cc-md-line">${_inlineMd(line)}</div>`);
        i++;
    }
    return out.join("");
}
